import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import SearchBar from '../../components/searchBar/SearchBar';
import Button from '../../components/buttons/Button';
import CustomLoading from '../../components/customLoading/CustomLoading';
import BlogTagCard from '../../components/blog/BlogTagCard';
import Pagination from '../../components/tables/pagination/Pagination';
import { confirmDanger } from '../../utils/confirmDialog';
import { getBlogTags, deleteBlogTag, patchBlogTag } from '../../services/blogTag';
import { toast } from 'react-toastify';
import { FaPlus } from 'react-icons/fa';

const BlogTags = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ results: [], count: 0, next: null, previous: null });
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);

    const fetchTags = async (query = '') => {
        setLoading(true);
        try {
            const response = await getBlogTags(query);
            if (response.status === 200) {
                setData(response.data);
            } else {
                toast.error('Failed to fetch tags');
            }
        } catch (error) {
            console.error('Error fetching tags:', error);
            toast.error('Failed to fetch tags');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (query) => {
        setSearchText(query);
        const searchQuery = query ? `?search=${query}` : '';
        fetchTags(searchQuery);
    };

    const handleCreateTag = () => {
        navigate('/blog/tags/create');
    };

    const handleEditTag = (tag) => {
        navigate(`/blog/tags/edit/${tag.id}`);
    };

    const handleDeleteTag = async (tag) => {
        const confirmation = await confirmDanger(
            `Are you sure you want to delete "${tag.name}"? This action cannot be undone.`,
            "Delete Tag"
        );

        if (confirmation) {
            try {
                const response = await deleteBlogTag(tag.id);
                if (response.status === 204) {
                    toast.success('Tag deleted successfully');
                    fetchTags(searchText ? `?search=${searchText}` : '');
                } else {
                    toast.error('Failed to delete tag');
                }
            } catch (error) {
                console.error('Error deleting tag:', error);
                toast.error('Failed to delete tag');
            }
        }
    };

    const handleToggleStatus = async (tag) => {
        try {
            const response = await patchBlogTag({
                id: tag.id,
                data: { is_active: !tag.is_active }
            });
            if (response.status === 200) {
                toast.success(`Tag ${!tag.is_active ? 'activated' : 'deactivated'} successfully`);
                fetchTags(searchText ? `?search=${searchText}` : '');
            } else {
                toast.error('Failed to update tag status');
            }
        } catch (error) {
            console.error('Error updating tag status:', error);
            toast.error('Failed to update tag status');
        }
    };


    useEffect(() => {
        fetchTags();
    }, []);

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Blog', link: '/blog/dashboard' },
                { name: 'Tags' },
            ]} />

            <div className="py-3">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                    <StyledHeadingText text="Blog Tags" />
                    <Button
                        onClick={handleCreateTag}
                        className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
                    >
                        <FaPlus size={16} />
                        <span>Add Tag</span>
                    </Button>
                </div>

                {/* Search Bar */}
                <div className="mb-6">
                    <SearchBar
                        placeholder="Search tags..."
                        onSearch={handleSearch}
                        value={searchText}
                    />
                </div>

                {/* Tags Grid */}
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <CustomLoading size={100} />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {data.results.map((tag) => (
                            <BlogTagCard
                                key={tag.id}
                                tag={tag}
                                onEdit={handleEditTag}
                                onDelete={handleDeleteTag}
                                onToggleStatus={handleToggleStatus}
                            />
                        ))}
                    </div>
                )}

                {!loading && data.results.length === 0 && (
                    <div className="text-center py-12">
                        <div className="text-gray-500 text-lg mb-4">No tags found</div>
                        <Button
                            onClick={handleCreateTag}
                            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary/90 transition-colors"
                        >
                            Create First Tag
                        </Button>
                    </div>
                )}

                {/* Pagination */}
                {data.count > 0 && (
                    <div className="mt-8">
                        <Pagination
                            currentPage={currentPage}
                            pageSize={pageSize}
                            totalEntries={data.count}
                            onPageChange={setCurrentPage}
                            data={data}
                            setData={setData}
                        />
                    </div>
                )}
            </div>


        </>
    );
};

export default BlogTags;
